import { useLocation, Link } from 'react-router-dom';
import { ChevronRightIcon, HomeIcon } from '@heroicons/react/24/outline';
import { useContext, useEffect, useState } from 'react';
import { ROUTE_LABEL_EVENT, routeLabel } from '../../utils/routeLabels';
import { IntegratedSidebarContext } from '../../contexts/IntegratedSidebarContext';

const ROUTE_LABELS = {
  'people-hr': 'People & HR',
  'new-hire': 'New hire',
  'my-goals': 'Reviews & Goals',
  ceo: 'CEO workspace',
  'exclusion-checks': 'Exclusion checks',
  admin: 'Dashboard', patients: 'Patients', caregivers: 'Providers', training: 'Training Videos',
  scheduler: 'Scheduler', billing: 'Billing', clinical: 'Clinical',
  reports: 'Reports', settings: 'Settings', alerts: 'Alerts',
  programs: 'Programs', 'visit-logs': 'Visit Logs',
  'visit-management': 'Visit Management', compliance: 'Compliance',
  waitlist: 'Waitlist', referrals: 'Referrals',
  'intake-pipeline': 'Intake Pipeline', 'da-scheduling': 'DA Scheduling',
  'adult-day-services': 'Adult Day Services', 'home-health': 'Home Health',
  '245d-basic': '245D Basic', '245d-intensive': '245D Intensive', '245d-readiness': '245D client readiness',
  '245d': '245D reports',
  'behavioral-health': 'Behavioral Health', 'co-responder': 'Co-Responder',
  // Compliance pages, named as the sidebar and the overview's cards are.
  'authorization-guardrails': 'Authorizations', 'pca-supervision': 'PCA Supervision',
};

// Pages whose address sits outside their section, shown under the section they belong to.
const ROUTE_PARENTS = {
  'exclusion-checks': { path: '/admin/people-hr', label: 'People & HR' },
};

// A segment that belongs to an area of its parent page, keyed "parent/segment". The area
// crumb comes first; the segment itself shows only as the last crumb (the new hire start
// form), so a profile under it reads "People & HR / Onboarding / <name>".
const ROUTE_AREAS = {
  'people-hr/new-hire': { path: '/admin/people-hr?area=ONBOARDING', label: 'Onboarding' },
};

// UUID v4-ish detector (good enough for path segment classification)
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Try to resolve a friendly label for a UUID path segment. Pages that render a
// named entity (a Program, Patient, etc.) should call
// `sessionStorage.setItem(\`breadcrumb:\${uuid}\`, friendlyName)` when their
// data loads. Otherwise we fall back to a neutral ellipsis so we never show
// a raw UUID or the mangled title-case version of one.
const resolveUuidLabel = (segment) => routeLabel(segment) || '···';

const humanize = (segment) =>
  segment.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const Breadcrumbs = () => {
  const location = useLocation();
  const { programs = [] } = useContext(IntegratedSidebarContext) || {};
  // A page registers a segment's real name after its data loads
  // (utils/routeLabels); re-render (and so re-read the labels) when it does.
  const [, setLabelVersion] = useState(0);
  useEffect(() => {
    const bump = () => setLabelVersion((v) => v + 1);
    window.addEventListener(ROUTE_LABEL_EVENT, bump);
    return () => window.removeEventListener(ROUTE_LABEL_EVENT, bump);
  }, []);

  const crumbs = (() => {
    const segments = location.pathname.split('/').filter(Boolean);
    if (segments.length <= 1) return [];
    return segments.flatMap((segment, index) => {
      const path = '/' + segments.slice(0, index + 1).join('/');
      const program = segments[index - 1] === 'programs' ? programs.find(entry =>
        entry.id === segment || (segment === '245d-basic' && entry.programType === '245D Basic')
        || (segment === '245d-intensive' && entry.programType === '245D Intensive')) : null;
      let label;
      if (program) {
        label = program.displayName || program.name;
      } else if (ROUTE_LABELS[segment]) {
        label = ROUTE_LABELS[segment];
      } else if (UUID_RE.test(segment)) {
        label = resolveUuidLabel(segment);
      } else {
        // A registered name ("WeCare Home Health") beats the title-cased slug.
        label = routeLabel(segment) || humanize(segment);
      }
      const isLast = index === segments.length - 1;
      const area = ROUTE_AREAS[`${segments[index - 1]}/${segment}`];
      if (area) return isLast ? [{ ...area, isLast: false }, { path, label, isLast }] : [{ ...area, isLast: false }];
      const parent = segments[index - 1] === 'admin' && ROUTE_PARENTS[segment];
      return parent ? [{ ...parent, isLast: false }, { path, label, isLast }] : [{ path, label, isLast }];
    });
  })();

  if (crumbs.length === 0) return null;

  return (
    <nav aria-label="Breadcrumb" className="mb-4">
      <ol className="flex items-center gap-1.5 text-sm list-none m-0 p-0">
        <li className="flex items-center">
          <Link to="/admin" className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" aria-label="Home">
            <HomeIcon className="w-4 h-4" />
          </Link>
        </li>
        {crumbs.slice(1).map(({ path, label, isLast }) => (
          <li key={path} className="flex items-center gap-1.5">
            <ChevronRightIcon className="w-3.5 h-3.5 text-gray-300 dark:text-gray-600" aria-hidden="true" />
            {isLast ? (
              <span className="text-gray-700 dark:text-gray-300 font-medium" aria-current="page">{label}</span>
            ) : (
              <Link to={path} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">{label}</Link>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
};

export default Breadcrumbs;
